import { Alert, Button, Group, Stack, Text, Tooltip } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { CancelTaskButton } from "./CancelTaskButton";
import { RerunTaskButton } from "./RerunTaskButton";
import type {
  TaskResponse,
  TaskStatusEventResponse,
} from "../features/queue/api";
import {
  escalationCause,
  type EscalationCause,
} from "../features/queue/escalationCause";
import { hasUnrecordedPrAttempt } from "../features/queue/unrecordedPrAttempt";

const BODY_KEY_BY_CAUSE: Readonly<Record<EscalationCause, string>> = {
  ci_retries_exhausted: "task.escalatedBannerBodyCiRetriesExhausted",
  review_rounds_exhausted: "task.escalatedBannerBodyReviewRoundsExhausted",
  awaiting_approval: "task.escalatedBannerBodyAwaitingApproval",
  other: "task.escalatedBannerBodyOther",
};

// Баннер «Требуется ваше внимание» для status=escalated.
// Текст выбирается по причине эскалации из журнала переходов; строка
// причины из `task.escalation_reason` выводится как есть.
// Четыре кнопки: rerun и cancel (рабочие), «поправить руками» (ссылка
// на PR в Forgejo), «запустить infra-agent'а» (disabled, TODO(#314)).
export function EscalatedBanner({
  task,
  events,
}: {
  readonly task: TaskResponse;
  readonly events: readonly TaskStatusEventResponse[];
}): ReactElement {
  const { t } = useTranslation();
  // Ссылки на PR нет — но это два разных случая: PR не создавали вовсе
  // либо создание начиналось и результат не записан (тогда PR мог
  // остаться на хостинге, и «нечего править руками» было бы неправдой).
  const hintWithoutPrUrl = hasUnrecordedPrAttempt(task)
    ? t("task.manualFixOrphanPrHint")
    : t("task.manualFixNoPrHint");
  const hint =
    task.pr_url === null ? hintWithoutPrUrl : t("task.manualFixHint");

  return (
    <Alert color="orange" title={t("task.escalatedBannerTitle")}>
      <Stack gap="sm">
        <Text>{t(BODY_KEY_BY_CAUSE[escalationCause(events)])}</Text>
        {task.escalation_reason !== null ? (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {t("task.escalatedBannerReason", {
              reason: task.escalation_reason,
            })}
          </Text>
        ) : null}
        <Group>
          <RerunTaskButton task={task} />
          <CancelTaskButton task={task} />
          <Tooltip label={hint} multiline w={320}>
            <Button
              component="a"
              variant="default"
              href={task.pr_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              disabled={task.pr_url === null}
            >
              {t("task.manualFixButton")}
            </Button>
          </Tooltip>
          <Tooltip label={t("task.infraAgentHint")} multiline w={320}>
            <Button variant="default" disabled>
              {t("task.infraAgentButton")}
            </Button>
          </Tooltip>
        </Group>
      </Stack>
    </Alert>
  );
}
